import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar,
  enqueueWarningSnackbar,
  enqueueInfoSnackbar,
} from '../actions/Notifications';

const notificationEntityIds = {
  filters: 'notifications.entity.filters',
  facets: 'notifications.entity.facets',
  'facet values': 'notifications.entity.facetValues',
  'value mappings': 'notifications.entity.valueMappings',
  synonyms: 'notifications.entity.synonyms',
  taxonomy: 'notifications.entity.taxonomy',
  'suggestion overrides': 'notifications.entity.suggestionOverrides',
  analytics: 'notifications.entity.analytics',
  'query analytics': 'notifications.entity.queryAnalytics',
};

/**
 * Hook for localized notifications
 *
 * Usage:
 * const notify = useLocalizedNotifications();
 * notify.error('notifications.error.invalidCredentials');
 * notify.success('notifications.success.saved');
 * notify.error('notifications.error.fetchingError', { entity: 'synonyms' });
 */
export const useLocalizedNotifications = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const formatMessage = (messageId, values = {}) => intl.formatMessage({ id: messageId }, values);

  return {
    error: (messageId, values = {}, duration = 2000) => {
      const message = formatMessage(messageId, values);
      dispatch(enqueueErrorSnackbar(message, duration));
    },

    success: (messageId, values = {}, duration = 2000) => {
      const message = formatMessage(messageId, values);
      dispatch(enqueueSuccessSnackbar(message, duration));
    },

    warning: (messageId, values = {}, duration = 2000) => {
      const message = formatMessage(messageId, values);
      dispatch(enqueueWarningSnackbar(message));
    },

    info: (messageId, values = {}, duration = 2000) => {
      const message = formatMessage(messageId, values);
      dispatch(enqueueInfoSnackbar(message));
    },
  };
};

/**
 * Helper function for sagas (where hooks can't be used)
 * Returns a localized message based on current locale from store
 *
 * Usage in saga:
 * import { getLocalizedMessage } from 'util/useLocalizedNotifications';
 * import { getLocale } from 'sagas/utils/localeSelectors';
 *
 * function* someSaga() {
 *   const locale = yield* getLocale();
 *   const message = getLocalizedMessage('notifications.error.cantGetData', {}, locale);
 *   yield put(enqueueErrorSnackbar(message));
 * }
 */
export const getLocalizedMessage = (messageId, values = {}, locale) => {
  const messages = {
    en: require('../lngProvider/locales/en_US.json'),
    de: require('../lngProvider/locales/de_DE.json'),
    es: require('../lngProvider/locales/es_ES.json'),
    fr: require('../lngProvider/locales/fr_FR.json'),
    it: require('../lngProvider/locales/it_IT.json'),
    pl: require('../lngProvider/locales/pl_PL.json'),
    pt: require('../lngProvider/locales/pt_PT.json'),
  };

  let effectiveLocale = locale;
  if (!effectiveLocale) {
    try {
      const { getStoredLocale } = require('./localeStorage');
      const localeData = getStoredLocale();
      effectiveLocale = localeData?.locale || 'en';
    } catch (e) {
      effectiveLocale = 'en';
    }
  }

  const localeMessages = messages[effectiveLocale] || messages.en;
  let message = localeMessages[messageId] || messageId;

  Object.keys(values).forEach((key) => {
    const entityId = key === 'entity' && messageId.startsWith('notifications.')
      ? notificationEntityIds[values[key]]
      : null;
    const value = (entityId && localeMessages[entityId]) || values[key];
    message = message.replace(new RegExp(`\\{${key}\\}`, 'g'), () => value);
  });

  return message;
};
